import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import aiMachineLearning from "@/assets/ai-machine-learning-bg.jpg";
import aiTechnology from "@/assets/ai-technology.jpg";
import aiWorkspace from "@/assets/ai-workspace-bg.jpg";
import analyticsDatabase from "@/assets/analytics-dashboard-bg.jpg";
import blockchainTech from "@/assets/blockchain-tech-bg.jpg";
import cloudComputing from "@/assets/cloud-computing-bg.jpg";
import cybersecurity from "@/assets/cybersecurity-bg.jpg";
import devopsAutomation from "@/assets/devops-automation-bg.jpg";
import digitalTransformation from "@/assets/digital-transformation-bg.jpg";
import enterpriseTech from "@/assets/enterprise-tech-bg.jpg";
import iotEcosystem from "@/assets/iot-ecosystem-bg.jpg";
import mobileApp from "@/assets/mobile-app-bg.jpg";

const TechCarousel = () => {
  const techImages = [
    {
      src: aiMachineLearning,
      title: "AI & Machine Learning",
      description: "Cutting-edge artificial intelligence and machine learning solutions to transform your business operations.",
      tags: ["AI", "ML", "Automation"]
    },
    {
      src: aiTechnology,
      title: "Advanced AI Technology",
      description: "Harness the power of advanced AI technologies for intelligent business automation and insights.",
      tags: ["AI", "Innovation", "Technology"]
    },
    {
      src: cloudComputing,
      title: "Cloud Computing Solutions",
      description: "Scalable cloud infrastructure and services to power your digital transformation journey.",
      tags: ["Cloud", "AWS", "Azure"]
    },
    {
      src: digitalTransformation,
      title: "Digital Transformation",
      description: "End-to-end digital transformation services to modernize your business processes and systems.",
      tags: ["Digital", "Transformation", "Modernization"]
    },
    {
      src: cybersecurity,
      title: "Cybersecurity Solutions",
      description: "Comprehensive cybersecurity services to protect your digital assets and ensure compliance.",
      tags: ["Security", "Protection", "Compliance"]
    },
    {
      src: blockchainTech,
      title: "Blockchain Technology",
      description: "Innovative blockchain solutions for secure, transparent, and decentralized business operations.",
      tags: ["Blockchain", "Web3", "Security"]
    },
    {
      src: devopsAutomation,
      title: "DevOps & Automation",
      description: "Streamlined development and deployment processes through advanced DevOps and automation practices.",
      tags: ["DevOps", "CI/CD", "Automation"]
    },
    {
      src: iotEcosystem,
      title: "IoT Ecosystem",
      description: "Connected IoT solutions to optimize operations and enable smart business environments.",
      tags: ["IoT", "Connected", "Smart"]
    },
    {
      src: analyticsDatabase,
      title: "Data Analytics",
      description: "Advanced data analytics and business intelligence solutions for data-driven decision making.",
      tags: ["Analytics", "Big Data", "BI"]
    },
    {
      src: enterpriseTech,
      title: "Enterprise Technology",
      description: "Comprehensive enterprise technology solutions to scale your business operations globally.",
      tags: ["Enterprise", "Scalability", "Solutions"]
    },
    {
      src: mobileApp,
      title: "Mobile Development",
      description: "Native and cross-platform mobile applications with cutting-edge user experiences.",
      tags: ["Mobile", "Apps", "Cross-platform"]
    },
    {
      src: aiWorkspace,
      title: "AI-Powered Workspace",
      description: "Intelligent workspace solutions powered by AI to enhance productivity and collaboration.",
      tags: ["AI Workspace", "Productivity", "Collaboration"]
    }
  ];

  return (
    <section className="py-10 bg-gradient-to-br from-background via-background/95 to-muted/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">
            Technology Innovation Showcase
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Explore our cutting-edge technology solutions that drive digital transformation and business innovation across industries.
          </p>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full max-w-6xl mx-auto"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {techImages.map((tech, index) => (
              <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card/80 backdrop-blur-sm">
                  <CardContent className="p-0">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={tech.src}
                        alt={tech.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h3 className="text-lg font-bold mb-2">{tech.title}</h3>
                        <p className="text-sm opacity-90 line-clamp-2 mb-3">
                          {tech.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {tech.tags.map((tag, tagIndex) => (
                            <Badge 
                              key={tagIndex} 
                              variant="secondary" 
                              className="text-xs bg-white/20 text-white border-white/30 hover:bg-white/30"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-12" />
          <CarouselNext className="hidden md:flex -right-12" />
        </Carousel>

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            Swipe or use navigation arrows to explore our technology solutions
          </p>
        </div>
      </div>
    </section>
  );
};

export default TechCarousel;